import React, { useMemo, useState } from 'react';

import { useResponsiveBreakpoints } from '../../../../hooks/useResponsiveBreakpoints';

import RichTextArea from '../../../../design-system/RichTextArea/RichTextArea';
import EditLineIcon from '../../../../design-system/Icons/EditLineIcon';
import ContentEditorModal from '../ContentEditorModal/ContentEditorModal';
import DeleteContentModal from '../DeleteContentModal/DeleteContentModal';
import { Button, ButtonIcon } from '../../../../design-system';
import {
    ArrowDownSLineIcon,
    ArrowUpSLineIcon,
    DeleteBin4LineIcon,
} from '../../../../design-system/Icons';

const ContentSection = ({ content, playbookTemplateId, isEditable, setPlaybookTemplate }) => {
    const sortedContent = useMemo(
        () => [...(content || []).sort((a, b) => a.order - b.order)],
        [content]
    );

    const [expandedSections, setExpandedSections] = useState({});
    const [actionModal, setActionModal] = useState({ action: null });

    const { isDesktop } = useResponsiveBreakpoints();

    const setContent = (cb) => {
        setPlaybookTemplate((template) => ({
            ...template,
            default_version: {
                ...template.default_version,
                details: {
                    ...template.default_version?.details,
                    content:
                        typeof cb === 'function'
                            ? cb(template.default_version?.details?.content || [])
                            : cb,
                },
            },
        }));
    };

    const toggleExpand = (order) =>
        setExpandedSections((prev) => ({ ...prev, [order]: !prev[order] }));

    const closeActionModal = () => setActionModal({ action: null });

    const isContent = !!sortedContent.length;

    return (
        <div className="flex flex-col gap-3">
            {isContent &&
                sortedContent.map((contentPiece) => {
                    const { order, name, type, detail } = contentPiece;

                    const isExpanded = !!expandedSections[order];
                    const icon = isExpanded ? ArrowUpSLineIcon : ArrowDownSLineIcon;

                    const isTextVisible = type === 'text' && (isExpanded || isDesktop);

                    return (
                        <div className="flex flex-col gap-2.5 group" key={order}>
                            <div className="flex gap-3 justify-between">
                                <p className="font-heading-bold text-heading-bold-m text-neutral-500">
                                    {name}
                                </p>

                                <div className="flex items-center gap-2 whitespace-nowrap">
                                    {isEditable && (
                                        <div className="flex items-center gap-2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                            <ButtonIcon
                                                type="link"
                                                size="xs"
                                                icon={EditLineIcon}
                                                onClick={() =>
                                                    setActionModal({
                                                        action: 'edit',
                                                        data: contentPiece,
                                                    })
                                                }
                                            />
                                            <ButtonIcon
                                                type="link"
                                                size="xs"
                                                icon={DeleteBin4LineIcon}
                                                onClick={() =>
                                                    setActionModal({
                                                        action: 'delete',
                                                        data: contentPiece,
                                                    })
                                                }
                                            />
                                        </div>
                                    )}
                                    {!isDesktop && (
                                        <ButtonIcon
                                            type="link"
                                            size="xs"
                                            icon={icon}
                                            onClick={() => toggleExpand(order)}
                                        />
                                    )}
                                </div>
                            </div>

                            {isTextVisible && (
                                <RichTextArea
                                    value={detail.text}
                                    previewOnly
                                    autoExpand
                                    previewMinHeight={32}
                                />
                            )}
                        </div>
                    );
                })}

            {!isContent && (
                <p className="font-body-bold text-body-bold-s text-neutral-500">No Content found</p>
            )}

            {isEditable && (
                <div className="flex justify-end">
                    <Button
                        type="link"
                        size="xs"
                        text="Add Content"
                        underlined
                        onClick={() => setActionModal({ action: 'create' })}
                    />
                </div>
            )}

            {(actionModal.action === 'create' || actionModal.action === 'edit') && (
                <ContentEditorModal
                    mode={actionModal.action}
                    data={actionModal.data}
                    contentArray={sortedContent}
                    playbookTemplateId={playbookTemplateId}
                    setContent={setContent}
                    onClose={closeActionModal}
                />
            )}

            {actionModal.action === 'delete' && (
                <DeleteContentModal
                    order={actionModal.data?.order}
                    name={actionModal.data?.name}
                    contentArray={sortedContent}
                    playbookTemplateId={playbookTemplateId}
                    setContent={setContent}
                    onClose={closeActionModal}
                />
            )}
        </div>
    );
};

export default ContentSection;
